<template>
  <HrPageLayout
    title="Training Applications"
    subtitle="Multi-stage training bio-data applications — committees, bonding letters and reports"
    icon="📋"
    :breadcrumb-items="breadcrumbItems"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-outline-primary" :disabled="loading" @click="refreshAll">
        <i class="bx" :class="loading ? 'bx-loader-alt bx-spin' : 'bx-refresh'"></i> Refresh
      </button>
      <button type="button" class="hr-btn hr-btn-outline-warning" :disabled="reminding" @click="runReturnReminders">
        <i class="bx bx-bell"></i> {{ reminding ? "Sending…" : "Return-to-work reminders" }}
      </button>
      <button type="button" class="hr-btn hr-btn-outline-success" :disabled="exporting" @click="exportCsv">
        <i class="bx bx-download"></i> CSV
      </button>
      <button type="button" class="hr-btn hr-btn-outline-success" :disabled="exportingPdf" @click="exportPdf">
        <i class="bx" :class="exportingPdf ? 'bx-loader-alt bx-spin' : 'bx-file'"></i> PDF
      </button>
    </template>

    <div class="ta-page">
      <div class="hr-content-grid grid-cards ta-stats">
        <div class="hr-chart-card"><div class="hr-chart-body"><p class="hr-text-muted">Total applications</p><h3>{{ stats.total || 0 }}</h3></div></div>
        <div class="hr-chart-card"><div class="hr-chart-body"><p class="hr-text-muted">Short-term</p><h3>{{ stats.short_term || 0 }}</h3></div></div>
        <div class="hr-chart-card"><div class="hr-chart-body"><p class="hr-text-muted">Long-term</p><h3>{{ stats.long_term || 0 }}</h3></div></div>
        <div class="hr-chart-card"><div class="hr-chart-body"><p class="hr-text-muted">In pipeline</p><h3>{{ stats.in_pipeline || 0 }}</h3></div></div>
        <div class="hr-chart-card"><div class="hr-chart-body"><p class="hr-text-muted">Approved / trained</p><h3>{{ stats.approved || 0 }}</h3></div></div>
        <div class="hr-chart-card"><div class="hr-chart-body"><p class="hr-text-muted">Rejected</p><h3>{{ stats.rejected || 0 }}</h3></div></div>
      </div>

      <div class="hr-chart-card hr-mt-4">
        <div class="hr-chart-header"><h5 class="hr-chart-title"><i class="bx bx-filter"></i> Filters & reports</h5></div>
        <div class="hr-chart-body filter-bar">
          <label class="filter-label">
            <span>Report set</span>
            <select v-model="filters.report" class="filter-select" @change="loadReports">
              <option value="pipeline">Full pipeline</option>
              <option value="approved">Approved / can train</option>
              <option value="rejected">Rejected</option>
              <option value="long_term">Long-term only</option>
              <option value="short_term">Short-term only</option>
              <option value="return_due">Return-to-work due</option>
            </select>
          </label>
          <label class="filter-label">
            <span>Stage (queue)</span>
            <select v-model="filters.stage" class="filter-select" @change="loadQueue">
              <option value="">My stages / all</option>
              <option v-for="s in stageOptions" :key="s.v" :value="s.v">{{ s.l }}</option>
            </select>
          </label>
          <label class="filter-label">
            <span>Term</span>
            <select v-model="filters.training_term" class="filter-select" @change="refreshAll">
              <option value="">All</option>
              <option value="short_term">Short-term (≤90 days)</option>
              <option value="long_term">Long-term (&gt;90 days)</option>
            </select>
          </label>
          <label class="filter-label">
            <span>Course contains</span>
            <input v-model="filters.course" type="text" class="filter-select" placeholder="Course title" @keyup.enter="loadReports">
          </label>
          <label class="filter-label">
            <span>Department</span>
            <input v-model="filters.department_name" type="text" class="filter-select" placeholder="Department" @keyup.enter="refreshAll">
          </label>
          <label class="filter-label">
            <span>Search staff / P.No</span>
            <input v-model="filters.search" type="text" class="filter-select" placeholder="Name or P/No" @keyup.enter="loadQueue">
          </label>
          <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="refreshAll">Apply</button>
        </div>
      </div>

      <div class="hr-chart-card hr-mt-4">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title"><i class="bx bx-list-check"></i> Review queue</h5>
          <span class="hr-text-muted hr-small">{{ queueMeta.total || 0 }} in view</span>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-wrap"><div class="hr-spinner"></div><p class="hr-text-muted">Loading…</p></div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Officer</th>
                  <th>Course</th>
                  <th>Dept</th>
                  <th>Term</th>
                  <th>Stage</th>
                  <th>Status</th>
                  <th>Path</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in queue" :key="row.id">
                  <td>{{ row.id }}</td>
                  <td>
                    <strong>{{ row.full_name }}</strong>
                    <div class="hr-text-muted hr-small">{{ row.emp_no }}</div>
                  </td>
                  <td>{{ row.course_title }}</td>
                  <td>{{ row.department_name || "—" }}</td>
                  <td>{{ termLabel(row.training_term) }}</td>
                  <td><span class="hr-badge hr-badge-info">{{ stageLabel(row.stage) }}</span></td>
                  <td>{{ row.status }}</td>
                  <td>{{ Number(row.is_health_path) === 1 ? "Health" : "Non-health" }}</td>
                  <td><button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="openDetail(row)">Open</button></td>
                </tr>
                <tr v-if="!queue.length">
                  <td colspan="9" class="hr-text-center hr-text-muted hr-py-4">No applications in your queues.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="queueMeta.totalPages > 1" class="hr-mt-3 hr-d-flex hr-gap-2">
            <button type="button" class="hr-btn hr-btn-sm" :disabled="!queueMeta.hasPrev" @click="page--; loadQueue()">Prev</button>
            <span class="hr-text-muted">Page {{ queueMeta.page }} / {{ queueMeta.totalPages }}</span>
            <button type="button" class="hr-btn hr-btn-sm" :disabled="!queueMeta.hasNext" @click="page++; loadQueue()">Next</button>
          </div>
        </div>
      </div>

      <div class="hr-chart-card hr-mt-4">
        <div class="hr-chart-header"><h5 class="hr-chart-title"><i class="bx bx-bar-chart-alt-2"></i> Report rows ({{ filters.report }})</h5></div>
        <div class="hr-chart-body">
          <div class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>P/No</th>
                  <th>Course</th>
                  <th>Dates</th>
                  <th>Term</th>
                  <th>Stage</th>
                  <th>Status</th>
                  <th>Dept</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in reportRows" :key="'r' + r.id">
                  <td>{{ r.full_name }}</td>
                  <td>{{ r.emp_no }}</td>
                  <td>{{ r.course_title }}</td>
                  <td>{{ r.course_from || "—" }} → {{ r.course_to || "—" }}</td>
                  <td>{{ termLabel(r.training_term) }}</td>
                  <td>{{ stageLabel(r.stage) }}</td>
                  <td>{{ r.status }}</td>
                  <td>{{ r.department_name }}</td>
                </tr>
                <tr v-if="!reportRows.length">
                  <td colspan="8" class="hr-text-center hr-text-muted hr-py-4">No rows for this report filter.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="(stats.by_course || []).length" class="hr-mt-4">
            <h6>By course</h6>
            <ul class="ta-by-list">
              <li v-for="c in stats.by_course" :key="c.course_title">
                <strong>{{ c.course_title }}</strong> — {{ c.cnt }} apps
                (approved {{ c.approved_cnt }}, LT {{ c.long_term_cnt }}, ST {{ c.short_term_cnt }})
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div v-if="detail" class="hr-modal-overlay" @click.self="closeDetail">
      <div class="hr-modal ta-modal">
        <div class="hr-modal-header">
          <h3>#{{ detail.application.id }} — {{ detail.application.course_title }}</h3>
          <button type="button" class="hr-modal-close" @click="closeDetail">&times;</button>
        </div>
        <div class="hr-p-3 detail-body">
          <p><strong>{{ detail.application.full_name }}</strong> · {{ detail.application.emp_no }} · {{ detail.application.department_name }}</p>
          <p class="hr-text-muted">
            Stage: <span class="hr-badge hr-badge-info">{{ stageLabel(detail.application.stage) }}</span>
            · Status: {{ detail.application.status }}
            · Path: {{ Number(detail.application.is_health_path) === 1 ? "Health (sub-county first)" : "Departmental first" }}
          </p>
          <p v-if="detail.application.course_from">
            Duration: {{ detail.application.course_from }} → {{ detail.application.course_to }}
            ({{ termLabel(detail.application.training_term) }}, {{ detail.application.duration_days || "—" }} days)
          </p>
          <p>Venue: {{ detail.application.venue || "—" }} · Mode: {{ detail.application.mode_of_study || "—" }} · Sponsor: {{ detail.application.sponsor || "—" }}</p>
          <p>Cost: {{ detail.application.total_cost != null ? "Ksh " + detail.application.total_cost : "—" }}</p>
          <div v-if="detail.application.supervisor_justification" class="hr-mt-2">
            <strong>Supervisor justification</strong>
            <p class="ta-pre">{{ detail.application.supervisor_justification }}</p>
          </div>
          <div v-if="detail.application.authorized_officer_justification" class="hr-mt-2">
            <strong>Authorized officer</strong>
            <p class="ta-pre">{{ detail.application.authorized_officer_justification }}</p>
          </div>
          <div v-if="detail.application.rejection_reason" class="hr-alert hr-alert-warning">
            Rejected: {{ detail.application.rejection_reason }}
          </div>

          <div class="hr-mt-3">
            <strong>Documents</strong>
            <ul v-if="detail.docs && detail.docs.length" class="ta-docs">
              <li v-for="d in detail.docs" :key="d.id">
                [{{ d.stage }}] {{ d.doc_type }} — {{ d.original_name || d.file_path }}
                <span class="hr-text-muted hr-small">by {{ d.uploaded_by }}</span>
              </li>
            </ul>
            <p v-else class="hr-text-muted">No documents yet.</p>
          </div>

          <div class="hr-mt-3">
            <strong>Timeline</strong>
            <ul class="ta-log">
              <li v-for="l in detail.log" :key="l.id">
                <span class="hr-text-muted">{{ l.created_at }}</span> — {{ l.action }}
                <span v-if="l.from_stage"> ({{ l.from_stage }} → {{ l.to_stage }})</span>
                <span v-if="l.details">: {{ l.details }}</span>
              </li>
            </ul>
          </div>

          <div class="hr-mt-3 ta-actions">
            <label class="filter-label ta-comments">
              <span>Comments / rejection reason</span>
              <textarea v-model="actionComments" rows="2" class="filter-select" placeholder="Optional notes or required rejection reason"></textarea>
            </label>

            <div v-if="isReviewStage" class="hr-d-flex hr-gap-2 hr-flex-wrap">
              <button type="button" class="hr-btn hr-btn-success" :disabled="acting" @click="doAction('approve_review')">
                Pass review → next stage
              </button>
              <button type="button" class="hr-btn hr-btn-danger" :disabled="acting" @click="doAction('reject')">Reject</button>
            </div>

            <div v-if="isDocsStage" class="hr-mt-2">
              <p class="hr-text-muted hr-small">Upload both minutes and committee report, then forward.</p>
              <div class="ta-upload-row">
                <label>
                  Minutes
                  <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" @change="onFile($event, 'minutes')" />
                </label>
                <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" :disabled="!pendingFiles.minutes || uploading" @click="uploadDoc('minutes')">
                  Upload minutes
                </button>
              </div>
              <div class="ta-upload-row">
                <label>
                  Report
                  <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" @change="onFile($event, 'report')" />
                </label>
                <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" :disabled="!pendingFiles.report || uploading" @click="uploadDoc('report')">
                  Upload report
                </button>
              </div>
              <div class="hr-d-flex hr-gap-2 hr-mt-2">
                <button type="button" class="hr-btn hr-btn-primary" :disabled="acting" @click="doAction('forward_after_docs')">
                  Forward with minutes & report
                </button>
                <button type="button" class="hr-btn hr-btn-danger" :disabled="acting" @click="doAction('reject')">Reject</button>
              </div>
            </div>

            <div v-if="isBondingStage" class="hr-mt-2">
              <p class="hr-text-muted">Head of Training: upload bonding letter — employee is notified to collect it and proceed to training.</p>
              <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" @change="onFile($event, 'bonding')" />
              <button type="button" class="hr-btn hr-btn-success hr-mt-2" :disabled="!pendingFiles.bonding || uploading" @click="uploadBonding">
                Upload bonding letter & approve
              </button>
            </div>
            <p v-if="actionMsg" class="hr-mt-2" :class="actionOk ? 'hr-text-success' : 'hr-text-danger'">{{ actionMsg }}</p>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { addCountyLetterhead, loadCountyLogoDataUrl } from "@/utils/payrollReconPdf";

const STAGE_LABELS = {
  draft: "Draft",
  sub_county_review: "Sub-county HR Committee",
  sub_county_docs: "Sub-county — minutes/report",
  departmental_review: "Departmental HR Committee",
  departmental_docs: "Dept HR — minutes/report",
  chrmac_review: "CHRMAC",
  chrmac_docs: "Head of Training → PSB package",
  psb_ceo_review: "PSB CEO",
  bonding_upload: "Bonding letter",
  approved: "Approved — proceed",
  rejected: "Rejected",
  in_training: "In training",
  completed: "Completed",
};

export default {
  name: "TrainingApplications",
  components: { HrPageLayout },
  data() {
    return {
      breadcrumbItems: [
        { label: "Home", to: "/" },
        { label: "Training Management", to: "/training-management" },
        { label: "Applications" },
      ],
      loading: false,
      reminding: false,
      exporting: false,
      exportingPdf: false,
      acting: false,
      uploading: false,
      letterhead: null,
      stats: {},
      queue: [],
      queueMeta: {},
      reportRows: [],
      page: 1,
      filters: {
        report: "pipeline",
        stage: "",
        training_term: "",
        course: "",
        department_name: "",
        search: "",
      },
      stageOptions: Object.keys(STAGE_LABELS).map((v) => ({ v, l: STAGE_LABELS[v] })),
      detail: null,
      actionComments: "",
      actionMsg: "",
      actionOk: false,
      pendingFiles: { minutes: null, report: null, bonding: null },
    };
  },
  computed: {
    stage() {
      return this.detail?.application?.stage || "";
    },
    isReviewStage() {
      return ["sub_county_review", "departmental_review", "chrmac_review", "psb_ceo_review"].includes(this.stage);
    },
    isDocsStage() {
      return ["sub_county_docs", "departmental_docs", "chrmac_docs"].includes(this.stage);
    },
    isBondingStage() {
      return this.stage === "bonding_upload";
    },
  },
  mounted() {
    this.refreshAll();
  },
  methods: {
    stageLabel(s) {
      return STAGE_LABELS[s] || s || "—";
    },
    termLabel(t) {
      if (t === "long_term") return "Long-term";
      if (t === "short_term") return "Short-term";
      return t || "—";
    },
    appendFilters(fd, includeSearch) {
      Object.keys(this.filters).forEach((k) => {
        if (k === "search" && !includeSearch) return;
        if (this.filters[k]) fd.append(k, this.filters[k]);
      });
    },
    async refreshAll() {
      await Promise.all([this.loadQueue(), this.loadReports()]);
    },
    async loadQueue() {
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "taListQueue");
        fd.append("page", String(this.page));
        fd.append("limit", "25");
        if (this.filters.stage) fd.append("stage", this.filters.stage);
        if (this.filters.training_term) fd.append("training_term", this.filters.training_term);
        if (this.filters.department_name) fd.append("department_name", this.filters.department_name);
        if (this.filters.search) fd.append("search", this.filters.search);
        const res = await execute(fd, authUrl);
        this.queue = res.data?.success ? (res.data.data?.applications || []) : [];
        this.queueMeta = res.data?.data?.pagination || {};
      } catch (_) {
        this.queue = [];
      } finally {
        this.loading = false;
      }
    },
    async loadReports() {
      try {
        const fd = new FormData();
        fd.append("function", "taGetReports");
        this.appendFilters(fd, false);
        const res = await execute(fd, authUrl);
        if (res.data?.success) {
          this.stats = res.data.data?.stats || {};
          this.reportRows = res.data.data?.rows || [];
        }
      } catch (_) {
        this.reportRows = [];
      }
    },
    async openDetail(row) {
      this.actionMsg = "";
      this.actionComments = "";
      this.pendingFiles = { minutes: null, report: null, bonding: null };
      try {
        const fd = new FormData();
        fd.append("function", "taGetApplication");
        fd.append("id", row.id);
        const res = await execute(fd, authUrl);
        if (res.data?.success) {
          this.detail = {
            application: res.data.data.application,
            docs: res.data.data.docs || [],
            log: res.data.data.log || [],
          };
        }
      } catch (e) {
        alert(e?.message || "Could not load application");
      }
    },
    closeDetail() {
      this.detail = null;
    },
    onFile(ev, key) {
      this.pendingFiles[key] = ev.target.files?.[0] || null;
    },
    async doAction(action) {
      if (!this.detail) return;
      if (action === "reject" && !this.actionComments.trim()) {
        this.actionOk = false;
        this.actionMsg = "Enter a rejection reason.";
        return;
      }
      this.acting = true;
      this.actionMsg = "";
      try {
        const fd = new FormData();
        fd.append("function", "taCommitteeAction");
        fd.append("id", this.detail.application.id);
        fd.append("action", action);
        fd.append("comments", this.actionComments);
        const res = await execute(fd, authUrl);
        this.actionOk = !!res.data?.success;
        this.actionMsg = res.data?.message || (this.actionOk ? "Done" : "Failed");
        if (this.actionOk) {
          await this.openDetail(this.detail.application);
          await this.refreshAll();
        }
      } catch (e) {
        this.actionOk = false;
        this.actionMsg = e?.message || "Action failed";
      } finally {
        this.acting = false;
      }
    },
    async uploadDoc(docType) {
      const file = this.pendingFiles[docType];
      if (!file || !this.detail) return;
      this.uploading = true;
      this.actionMsg = "";
      try {
        const fd = new FormData();
        fd.append("function", "taUploadStageDocs");
        fd.append("id", this.detail.application.id);
        fd.append("doc_type", docType);
        fd.append("file", file);
        const res = await execute(fd, authUrl);
        this.actionOk = !!res.data?.success;
        this.actionMsg = res.data?.message || "Uploaded";
        if (this.actionOk) {
          this.pendingFiles[docType] = null;
          await this.openDetail(this.detail.application);
        }
      } catch (e) {
        this.actionOk = false;
        this.actionMsg = e?.message || "Upload failed";
      } finally {
        this.uploading = false;
      }
    },
    async uploadBonding() {
      const file = this.pendingFiles.bonding;
      if (!file || !this.detail) return;
      this.uploading = true;
      try {
        const fd = new FormData();
        fd.append("function", "taUploadBondingLetter");
        fd.append("id", this.detail.application.id);
        fd.append("file", file);
        const res = await execute(fd, authUrl);
        this.actionOk = !!res.data?.success;
        this.actionMsg = res.data?.message || "Done";
        if (this.actionOk) {
          await this.openDetail(this.detail.application);
          await this.refreshAll();
        }
      } catch (e) {
        this.actionOk = false;
        this.actionMsg = e?.message || "Upload failed";
      } finally {
        this.uploading = false;
      }
    },
    async runReturnReminders() {
      this.reminding = true;
      try {
        const fd = new FormData();
        fd.append("function", "taProcessReturnReminders");
        const res = await execute(fd, authUrl);
        alert(res.data?.message || "Done");
        await this.refreshAll();
      } catch (e) {
        alert(e?.message || "Failed");
      } finally {
        this.reminding = false;
      }
    },
    exportCsv() {
      this.exporting = true;
      try {
        const headers = ["ID", "Name", "P/No", "Course", "From", "To", "Term", "Stage", "Status", "Department", "Path"];
        const lines = [headers.join(",")];
        (this.reportRows || []).forEach((r) => {
          const cells = [
            r.id,
            r.full_name,
            r.emp_no,
            r.course_title,
            r.course_from,
            r.course_to,
            r.training_term,
            r.stage,
            r.status,
            r.department_name,
            Number(r.is_health_path) === 1 ? "Health" : "Non-health",
          ].map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`);
          lines.push(cells.join(","));
        });
        const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `training-applications-${this.filters.report}-${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      } finally {
        this.exporting = false;
      }
    },
    async exportPdf() {
      this.exportingPdf = true;
      try {
        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
        const logoDataUrl = await loadCountyLogoDataUrl(this.letterhead);
        const startY = addCountyLetterhead(doc, {
          logoDataUrl,
          title: "Training Applications Report",
          subtitle: `Report: ${this.filters.report} · Generated ${new Date().toLocaleString()}`,
        });
        const body = (this.reportRows || []).map((r) => [
          r.full_name || "",
          r.emp_no || "",
          r.course_title || "",
          `${r.course_from || ""} – ${r.course_to || ""}`,
          this.termLabel(r.training_term),
          this.stageLabel(r.stage),
          r.status || "",
          r.department_name || "",
        ]);
        autoTable(doc, {
          startY: startY + 8,
          head: [["Name", "P/No", "Course", "Dates", "Term", "Stage", "Status", "Dept"]],
          body,
          styles: { fontSize: 8, cellPadding: 3 },
          headStyles: { fillColor: [15, 118, 110] },
          margin: { left: 36, right: 36 },
        });
        let y = doc.lastAutoTable.finalY + 16;
        doc.setFontSize(10);
        doc.text(
          `Summary — Total: ${this.stats.total || 0} | Short: ${this.stats.short_term || 0} | Long: ${this.stats.long_term || 0} | Approved: ${this.stats.approved || 0} | Pipeline: ${this.stats.in_pipeline || 0}`,
          36,
          y
        );
        doc.save(`training-applications-${this.filters.report}.pdf`);
      } catch (e) {
        alert(e?.message || "PDF export failed");
      } finally {
        this.exportingPdf = false;
      }
    },
  },
};
</script>

<style scoped>
.ta-stats { grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); }
.filter-bar { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.filter-label { display: flex; flex-direction: column; gap: 4px; font-size: 0.8rem; font-weight: 600; color: #334155; }
.filter-select { min-width: 140px; padding: 6px 10px; border: 1px solid #cbd5e1; border-radius: 6px; font-weight: 400; }
.ta-modal { max-width: 760px; max-height: 90vh; overflow: auto; }
.ta-pre { white-space: pre-wrap; background: #f8fafc; padding: 8px; border-radius: 6px; font-size: 0.9rem; }
.ta-docs, .ta-log, .ta-by-list { margin: 8px 0; padding-left: 18px; }
.ta-upload-row { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin: 8px 0; }
.ta-comments { width: 100%; }
.ta-comments .filter-select { width: 100%; min-height: 60px; }
.hr-flex-between { display: flex; justify-content: space-between; align-items: center; }
.hr-d-flex { display: flex; }
.hr-flex-wrap { flex-wrap: wrap; }
.hr-gap-2 { gap: 8px; }
</style>
